import {
  afterNextRender,
  computed,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  Injector,
  output,
  signal
} from '@angular/core';
import { NG_VALIDATORS } from '@angular/forms';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs';

import { ValueAccessorProviderFactory } from '../../value-accessor/value-accessor-provider-factory';
import { DateTimeFormat } from '../../../services/date/date-formatter.service';

import { DatePickerService } from './date-picker.service';

import type {
  OnInit,
  InputSignal,
  OutputEmitterRef,
  Signal,
  WritableSignal
} from '@angular/core';
import type { UntypedFormControl, Validator, ControlValueAccessor, ValidationErrors } from '@angular/forms';
import type { Subscription } from 'rxjs';

import type { DatePickerFormatType } from '../../../services/date/date-formatter.service';

import type { DatePickerHost } from './date-picker.contract';
import type { DateValidator } from './date-validator.type';

@Directive({
  selector: 'input[tdWbDatePicker]',
  providers: [
    ValueAccessorProviderFactory(DatePickerInputDirective),
    {
      provide: NG_VALIDATORS,
      useExisting: DatePickerInputDirective,
      multi: true
    }
  ],
  host: {
    '(change)': 'onChange($event.target)',
    '(input)': 'onInput($event.target)',
    '(blur)': 'onBlur()',
    '[attr.disabled]': 'disabledState() || null',
    '[attr.required]': 'requiredInput() || null',
    '[attr.maxlength]': 'getDateFormat().length',
    '[attr.placeholder]': 'computedPlaceholder()'
  }
})
export class DatePickerInputDirective implements ControlValueAccessor, OnInit, Validator {

  // ── DI ──────────────────────────────────────────────────────────────────
  private readonly elementRef = inject(ElementRef);
  private readonly datePickerService = inject(DatePickerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  // ── Outputs ──────────────────────────────────────────────────────────────
  readonly valueChange: OutputEmitterRef<Date | null> = output<Date | null>();
  readonly disabledChange: OutputEmitterRef<boolean> = output<boolean>();

  // ── Signal inputs ────────────────────────────────────────────────────────
  /** Function which should return true if date in the selector should be disabled for selection. */
  readonly disabledDates: InputSignal<DateValidator> = input<DateValidator>(undefined);

  /** If true, weekends cannot be selected; defaults to true */
  readonly noWeekends: InputSignal<boolean> = input<boolean>(true);

  /** Format to parse date entered by user; defaults to yyyy-MM-dd */
  readonly format: InputSignal<DatePickerFormatType> = input<DatePickerFormatType>(DateTimeFormat.YEAR_MONTH_DAY);

  /** Placeholder; defaults to the date format */
  readonly placeholder: InputSignal<string> = input<string>(undefined);

  /** Oldest selectable date */
  readonly minDate: InputSignal<Date> = input<Date>(undefined);

  /** Latest selectable date */
  readonly maxDate: InputSignal<Date> = input<Date>(undefined);

  /** Binds this directive to its DatePickerComponent host. */
  readonly tdWbDatePicker: InputSignal<DatePickerHost> = input<DatePickerHost>(undefined);

  /** Disabled state requested declaratively via the `disabled` attribute/binding. */
  readonly disabledInput: InputSignal<boolean> = input<boolean>(false, { alias: 'disabled' });

  /** Disabled state pushed imperatively by a form owner through `setDisabledState()`. */
  private readonly disabledByOwner: WritableSignal<boolean> = signal(false);

  /**
   * Effective disabled state.
   *
   * Angular Forms (reactive and Signal Forms) push disabled state through
   * `setDisabledState()`, while templates may also bind `[disabled]`. Because this
   * directive provides `NG_VALUE_ACCESSOR` it replaces `DefaultValueAccessor`, so
   * nothing else applies the disabled property on our behalf — both sources must be
   * reconciled here. See `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md` §4.2.
   */
  readonly disabledState: Signal<boolean> = computed(
    (): boolean => this.disabledInput() || this.disabledByOwner()
  );

  /** Id of the associated input element. */
  readonly idInput: InputSignal<string> = input<string>(undefined, { alias: 'id' });

  /** Whether the field is required. */
  readonly requiredInput: InputSignal<boolean> = input<boolean>(false, { alias: 'required' });

  get disabled(): boolean { return this.disabledState(); }
  get id(): string { return this.idInput() ?? ''; }
  get required(): boolean { return !!this.requiredInput(); }

  // ── Value property ────────────────────────────────────────────────────────
  get value(): Date | null { return this._value; }

  /**
   * Control -> owner. Notifies Angular Forms unless we are currently applying an
   * owner-initiated `writeValue()`, which must never echo back (§4.2 criterion 4).
   */
  set value(value: Date | null) {
    const oldDate = this.value;
    this._value = value;
    if (!this.suppressOnChange) {
      this.onChangeCallback(value);
    }

    if (oldDate !== value) {
      // valueChange is control -> DatePickerComponent, not control -> owner,
      // so it must still fire while writing from the owner.
      this.valueChange.emit(value);
    }
  }

  /**
   * Array of validator functions for model value
   */
  disabledDateValidators: Array<DateValidator> = [];

  // ── Private state ─────────────────────────────────────────────────────────
  private datepicker: DatePickerHost;
  private datepickerSubscription: Subscription;
  private _value: Date | null;
  private formatted: string;
  private isEmpty: boolean;
  private onNgChange: () => void;
  private focusEffectSetup: boolean = false;
  /** True while applying an owner-initiated write, to suppress the onChange echo. */
  private suppressOnChange: boolean = false;
  constructor() {
    // effect: calls Angular Forms' onNgChange() when constraint inputs change so
    // the validator re-runs. queueMicrotask defers outside the CD cycle to prevent
    // NG0103 infinite loops with OnPush + signal inputs + Angular Forms.
    let initialized = false;
    effect(() => {
      this.disabledDates();
      this.noWeekends();
      this.format();
      this.placeholder();
      this.minDate();
      this.maxDate();
      this.requiredInput(); // re-validate when required changes

      if (!initialized) {
        initialized = true;
        return;
      }
      if (this.onNgChange !== undefined) {
        queueMicrotask(() => this.onNgChange?.());
      }
    });

    // Wire up the DatePickerHost when bound via [tdWbDatePicker]="...".
    effect(() => {
      const picker = this.tdWbDatePicker();
      if (!picker) return;
      this.datepickerSubscription?.unsubscribe();
      this.datepicker = picker;
      this.datepicker.registerInput(this);
      this.datepickerSubscription = this.datepicker.selectedChanged
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((selected: Date): void => {
          // Control -> owner: a calendar click is a user interaction, not an
          // owner-initiated write, so (unlike `writeValue()`) it must echo through
          // `onChangeCallback` or the bound form control's value never updates —
          // see `applySelectedDate()`.
          this.applySelectedDate(selected);
        });
      // setupFocusEffect calls toObservable() which internally creates an effect().
      // Angular forbids nested effect creation inside a reactive context — defer via
      // queueMicrotask to schedule it in a fresh (non-reactive) execution context.
      queueMicrotask(() => this.setupFocusEffect());
    });

    // Emit disabledChange reactively when the effective disabled state changes.
    toObservable(this.disabledState)
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((newValue: boolean) => {
        this.disabledChange.emit(newValue);
      });

    this.destroyRef.onDestroy(() => {
      if (this.datepickerSubscription) {
        this.datepickerSubscription.unsubscribe();
      }
    });
  }

  /**
   * Computed placeholder for host binding
   */
  computedPlaceholder(): string {
    const format = this.datePickerService.getPlaceholderFormat(this.getDateFormat());
    return this.placeholder() ?? format.toLocaleUpperCase();
  }

  onChange(target: EventTarget | null): void {
    const text = (target as HTMLInputElement)?.value;
    this.isEmpty = text.length === 0;
    // Committing (blur/change) unparseable text clears the field. This must happen here
    // rather than relying on a `writeValue` echo bouncing back through a shared model —
    // see `date-picker-shared-model.spec.ts`.
    this.setValue(this.parseStringToDate(text));
  }

  onInput(target: EventTarget | null): void {
    const text = (target as HTMLInputElement)?.value;
    this.isEmpty = text.length === 0;
    this.parser(text);
  }

  /** Marks the control touched when the user leaves the field. */
  onBlur(): void {
    this.onTouchedCallback();
  }

  ngOnInit(): void {
    this.setValidators();
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onNgChange = fn;
  }

  // Implemented as part of ControlValueAccessor.
  writeValue(value: Date): void {
    // Owner -> control. Must not echo through onChangeCallback (§4.2 criterion 4);
    // echoing `undefined` here removes the property from a Signal Forms model and
    // orphans the field (NG01902).
    this.suppressOnChange = true;
    try {
      this.isEmpty = !this.datePickerService.isDate(value);
      this.setValue(value);
    } finally {
      this.suppressOnChange = false;
    }
  }

  /**
   * Control -> owner: applies a date picked from the calendar overlay. Deliberately
   * distinct from `writeValue()` — a calendar click is a user interaction and must
   * notify Angular Forms via `onChangeCallback`, otherwise the bound control's value
   * never updates and constraint validators (e.g. `minDate`) see a stale/undefined
   * `formControl.value`.
   */
  private applySelectedDate(value: Date): void {
    this.isEmpty = !this.datePickerService.isDate(value);
    this.setValue(value);
  }

  // Implemented as part of ControlValueAccessor.
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChangeCallback = fn;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  /**
   * Implemented as part of ControlValueAccessor.
   *
   * Required: providing NG_VALUE_ACCESSOR replaces Angular's DefaultValueAccessor,
   * so without this a `control.disable()` would leave the input fully editable.
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabledByOwner.set(isDisabled);
  }

  /**
   * Validator function for date picker
   */
  validate(formControl: UntypedFormControl): ValidationErrors | null {
    if (this.isEmpty && this.required) {
      return { required: true };
    }
    if (!this.isEmpty && this.getParsed() === null) {
      return { parse: true };
    }
    if (
      this.datePickerService.isDate(this.minDate()) &&
      this.getParsed() !== null &&
      this.datePickerService.validateMinDate(this.minDate())(formControl.value)
    ) {
      return { minDate: true };
    }
    if (
      this.datePickerService.isDate(this.maxDate()) &&
      this.getParsed() !== null &&
      this.datePickerService.validateMaxDate(this.maxDate())(formControl.value)
    ) {
      return { maxDate: true };
    }
    if (
      this.getParsed() !== null &&
      this.isDisabledDate(formControl.value) &&
      !this.datePickerService.validateNoWeekends()(formControl.value)
    ) {
      return { noWeekends: true };
    }
    if (this.getParsed() !== null && this.isDisabledDate(formControl.value)) {
      return { disabledDate: true };
    }
    return null;
  }

  /**
   * This function checks if a date is disabled
   */
  isDisabledDate(date: Date): boolean {
    return this.datePickerService.isDisabledDate(date, this.disabledDates(), this.disabledDateValidators);
  }

  /**
   * Returns the date format
   */
  getDateFormat(): DatePickerFormatType {
    return this.datePickerService.getDateFormat(this.format());
  }

  private onChangeCallback: (_: unknown) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};

  private setupFocusEffect(): void {
    if (this.focusEffectSetup || !this.datepicker) {
      return;
    }
    this.focusEffectSetup = true;
    // Use toObservable synchronously (no dynamic import needed — injector is already available).
    toObservable(this.datepicker.focusRequested, { injector: this.injector })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((shouldFocus: boolean) => {
        if (shouldFocus) {
          this.doFocus();
          this.datepicker.focusRequested.set(false);
        }
      });
  }

  /**
   * Sets ngModel value for datepicker.
   *
   * Anything that is not a usable `Date` — `null`, `undefined`, or text that failed to
   * parse — clears both the model and the rendered text. There is deliberately no
   * "keep the invalid text" mode: the previous `setToDefault` flag left stale text in the
   * field and depended on a `writeValue` echo to clean it up.
   */
  private setValue(date: Date | null | undefined): void {
    if (date !== undefined && date !== null && this.datePickerService.isDate(date)) {
      this.setDateValue(date);
    } else {
      this.setEmptyValue();
    }
  }

  /**
   * This function parses string to date if valid. Otherwise returns null
   */
  private parseStringToDate(text: string): Date | undefined {
    let result: Date;
    let valid: boolean;
    let date: Date;
    if (text.length !== 0) {
      valid = this.datePickerService.validateDatePattern(this.format(), text);
      if (valid) {
        date = this.datePickerService.parseDate(text, this.format());
        if (date !== null) {
          result = date;
        }
      }
    }
    return result;
  }

  /**
   * Adds necessary validators for date picker
   */
  private setValidators(): void {
    if (this.noWeekends()) {
      this.registerDisabledDateValidator(this.datePickerService.validateNoWeekends());
    }
  }

  /**
   * Register validators to disabledDateValidators
   */
  private registerDisabledDateValidator(disabledDateValidator: DateValidator): void {
    this.disabledDateValidators.push(disabledDateValidator);
  }

  /**
   * This getter returns parsed model value.
   */
  private getParsed(): Date | null {
    return this.datePickerService.isDate(this.value) ? this.value : null;
  }

  private doFocus(): void {
    afterNextRender(() => {
      this.elementRef.nativeElement.focus();
    }, { injector: this.injector });
  }

  /**
   * This function is parser for the text input element
   */
  private parser(text: string): void {
    const date = this.parseStringToDate(text);
    if (date !== undefined) {
      this.setDateValue(date);
    } else if (this.isEmpty) {
      this.setEmptyValue();
    }
  }

  private setDateValue(date: Date): void {
    this.value = date;
    this.formatted = this.datePickerService.formatDate(this.value, this.format());
    this.elementRef.nativeElement.value = this.formatted;
  }

  private setEmptyValue(): void {
    // `null`, not `undefined`: Angular Forms writes null for an empty control, and
    // writing `undefined` into a Signal Forms model deletes the property (§4.2 criterion 5).
    this.value = null;
    this.formatted = '';
    this.elementRef.nativeElement.value = this.formatted;
  }
}
